import { proxyRefs, reactive, ref, toRefs, type Component, type Ref } from 'vue';
import { DialogVariants, type DialogConfig, type DialogVariant } from '@/constants/dialog';

interface DialogState {
    title: string;
    message: string;
    variant: DialogVariant;
    actionText: string;
    actionIcon: Component | null;
    cancelText: string;
    cancelIcon: Component | null;
    hideNoButton: boolean;
    data: unknown;
}

interface PromiseResolvers {
    resolve: (value: boolean) => void;
}

const DEFAULT_STATE: DialogState = {
    title: '',
    message: '',
    variant: DialogVariants.Danger,
    actionText: 'Oke',
    actionIcon: null,
    cancelText: 'Annuleren',
    cancelIcon: null,
    hideNoButton: false,
    data: null,
};

const active: Ref<boolean> = ref(false);
const loading: Ref<boolean> = ref(false);
const resolver: Ref<PromiseResolvers | null> = ref(null);
const state = reactive<DialogState>({ ...DEFAULT_STATE });

const resetState = (): void => {
    Object.assign(state, DEFAULT_STATE);
    active.value = false;
    loading.value = false;
    resolver.value = null;
};

const open = (options: DialogConfig): Promise<boolean> => {
    if (active.value) {
        console.warn('Dialog is already open');
        return Promise.resolve(false);
    }

    if (!options.title || !options.message) {
        console.error('Dialog requires both title and message');
        return Promise.reject(new Error('Invalid dialog configuration'));
    }

    active.value = true;

    Object.assign(state, {
        title: options.title,
        message: options.message,
        variant: options.variant ?? DEFAULT_STATE.variant,
        actionText: options.actionText ?? DEFAULT_STATE.actionText,
        actionIcon: options.actionIcon ?? DEFAULT_STATE.actionIcon,
        cancelText: options.cancelText ?? DEFAULT_STATE.cancelText,
        cancelIcon: options.cancelIcon ?? DEFAULT_STATE.cancelIcon,
        hideNoButton: options.hideNoButton ?? DEFAULT_STATE.hideNoButton,
        data: options.data ?? DEFAULT_STATE.data,
    });

    return new Promise<boolean>((resolve) => {
        resolver.value = { resolve };
    });
};

const close = (): void => {
    resolver.value?.resolve(false);
    active.value = false;
};

const confirm = (result = true): void => {
    resolver.value?.resolve(result);
    active.value = false;
};

const setLoading = (isLoading: boolean): void => {
    loading.value = isLoading;
};

export const useConfirmDialog = () => proxyRefs({
        active,
        loading,
        ...toRefs(state),
    open,
    close,
    confirm,
    setLoading,
    resetState,
});
